import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type Order, type PlanningBoard, type PlanningItem } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { serviceRange } from './OrdersPage';
import './flow.css';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

function Readiness({ item }: { item: PlanningItem }) {
  const r = item.readiness;
  const chip = (ok: boolean, text: string) => <li className={ok ? 'is-ok' : 'is-open'}><span aria-hidden="true">{ok ? '✓' : '○'}</span><span>{text}</span></li>;
  return (
    <ul className="flow-checks" aria-label="Stand der Planung">
      {chip(r.hasServiceDates, r.hasServiceDates ? 'Leistungszeitraum eingetragen' : 'Leistungszeitraum fehlt')}
      {chip(r.tasks > 0, r.tasks > 0 ? `${r.tasks} ${r.tasks === 1 ? 'Aufgabe' : 'Aufgaben'}` : 'Noch keine Aufgabe')}
      {chip(r.tasks > 0 && r.tasksAssigned === r.tasks, `${r.tasksAssigned} von ${r.tasks} Aufgaben mit Zuständigem`)}
      <li className={r.shifts > 0 ? 'is-ok' : 'is-info'}><span aria-hidden="true">{r.shifts > 0 ? '✓' : '·'}</span><span>{r.shifts > 0 ? `${r.shifts} im Schichtplan` : 'Nicht im Schichtplan'}</span></li>
      {r.vehicles !== null && <li className={r.vehicles > 0 ? 'is-ok' : 'is-info'}><span aria-hidden="true">{r.vehicles > 0 ? '✓' : '·'}</span><span>{r.vehicles > 0 ? `${r.vehicles} Fahrzeug${r.vehicles === 1 ? '' : 'e'}` : 'Kein Fahrzeug'}</span></li>}
    </ul>
  );
}

/**
 * Auftragsplanung (KON-172), Arbeitsplatz des Personalmanagements: Aufträge, die die Auftragsabwicklung übergeben hat, mit Hinweis, Termin und Stand der Planung. Von hier öffnet man den
 * Auftrag und plant Aufgaben, Mitarbeiter und Fahrzeuge, schließt die Planung ab oder gibt den Auftrag mit einer Frage zurück. Darunter stehen die geplanten Aufträge bis zur Umsetzung.
 */
export function PlanningPage() {
  const client = useQueryClient();
  const { can } = useSession();
  const [returning, setReturning] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const board = useQuery({ queryKey: ['order-planning'], queryFn: async () => (await api<{ data: PlanningBoard }>('GET', '/orders/planning')).data, refetchInterval: 60_000, refetchOnWindowFocus: true });

  const refresh = () => {
    for (const key of ['order-planning', 'order-pipeline', 'orders', 'order']) client.invalidateQueries({ queryKey: [key] });
  };
  const act = useMutation({
    mutationFn: async ({ id, kind }: { id: string; kind: 'complete' | 'return' }) =>
      (await api<{ data: Order }>('POST', kind === 'complete' ? `/orders/${id}/planning/complete` : `/orders/${id}/handover/return`, kind === 'return' ? { reason: reason.trim() } : undefined)).data,
    onSuccess: (o, v) => {
      refresh();
      setReturning(null);
      setReason('');
      setMessage({ ok: true, text: v.kind === 'complete' ? `${o.number} ist geplant.` : `${o.number} ging mit Ihrer Frage an die Auftragsabwicklung zurück.` });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das hat nicht geklappt.') }),
  });
  const canPlan = can('orders.plan');
  const queue = board.data?.queue ?? [];
  const planned = board.data?.planned ?? [];

  const card = (item: PlanningItem, inQueue: boolean) => (
    <article key={item.id} className="card plan-item" aria-labelledby={`plan-${item.id}`}>
      <div className="plan-item__head">
        <h3 id={`plan-${item.id}`}><Link to={`/auftraege/${item.id}`} className="strong-link">{item.number}</Link> · {item.subject}</h3>
        <span className="plan-item__meta">{item.contactName} · {serviceRange(item.serviceStart, item.serviceEnd)}</span>
      </div>
      {item.handedOverAt && <p className="muted small">Übergeben am {stamp.format(new Date(item.handedOverAt))} Uhr{item.handedOverByName ? ` von ${item.handedOverByName}` : ''}.</p>}
      {item.handoverNote && <blockquote className="flow-note">{item.handoverNote}</blockquote>}
      <Readiness item={item} />
      <div className="plan-item__actions">
        <Link className="btn btn--secondary btn--sm" to={`/auftraege/${item.id}`}>Auftrag öffnen und planen</Link>
        {canPlan && inQueue && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={act.isPending}
            onClick={() => (item.readiness.ready || window.confirm('Die Planung ist noch nicht vollständig. Trotzdem abschließen?')) && act.mutate({ id: item.id, kind: 'complete' })}
          >
            Planung abschließen
          </button>
        )}
        {canPlan && item.handedOverAt && <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setReturning(returning === item.id ? null : item.id); setReason(''); }}>Mit Frage zurückgeben …</button>}
      </div>
      {returning === item.id && (
        <form className="flow-return" onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 2) act.mutate({ id: item.id, kind: 'return' }); }}>
          <label className="field">
            <span>Frage oder Grund für die Auftragsabwicklung</span>
            <textarea className="input textarea textarea--short" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} required />
          </label>
          <div className="form-actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={act.isPending || reason.trim().length < 2}>Zurückgeben</button>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setReturning(null)}>Abbrechen</button>
          </div>
        </form>
      )}
    </article>
  );

  return (
    <>
      <div className="page-head">
        <h1>Auftragsplanung</h1>
        <span className="muted">Übergebene Aufträge planen</span>
      </div>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {board.isError && <div className="alert alert--error" role="alert">{(board.error as Error).message}</div>}
      <section aria-labelledby="plan-queue">
        <h2 id="plan-queue">Wartet auf Planung <span className="muted">({queue.length})</span></h2>
        <p className="muted small">Die Auftragsabwicklung hat diese Aufträge übergeben. Plant die Umsetzung bis zum Ende: Aufgaben anlegen, Mitarbeiter und Fahrzeuge zuordnen. Dann die Planung abschließen.</p>
        <div className="plan-list">
          {board.isLoading && <p className="muted">Wird geladen …</p>}
          {!board.isLoading && queue.length === 0 && <div className="card"><p className="muted">Nichts zu planen. Neue Übergaben erscheinen hier.</p></div>}
          {queue.map((item) => card(item, true))}
        </div>
      </section>
      <section aria-labelledby="plan-done">
        <h2 id="plan-done">Geplant <span className="muted">({planned.length})</span></h2>
        <p className="muted small">Bereit zur Umsetzung, der nächste Beginn zuerst.</p>
        <div className="plan-list">
          {planned.length === 0 && !board.isLoading && <div className="card"><p className="muted">Keine geplanten Aufträge in Wartestellung.</p></div>}
          {planned.map((item) => card(item, false))}
        </div>
      </section>
    </>
  );
}
